import { booking } from '../api/booking';
import { isApiConfigured, tokenStore } from '../api';
import { publicSettings } from '../api/publicSettings';
import { haversineKm } from './routeEstimate';

/**
 * InGo's own places (the mobile plan's M4: this one seam serves every
 * screen), when this phone holds an InGo session: its map server, its service
 * area, no Google key. InGo's place search is the app's only one (2 Oct
 * 2026). Null when there is no session or the call failed, and then the
 * caller has no place to give; an empty answer from InGo is an answer.
 */
async function ingoPlaces(run) {
  try {
    if (!isApiConfigured() || !(await tokenStore.read())) return null;

    return { value: await run() };
  } catch (e) {
    console.warn(`[places] InGo places failed ${JSON.stringify({ code: e?.code || null, message: e?.message || String(e) })}`);

    return null;
  }
}

/** One of InGo's places as the screens' one-line label. */
function ingoPlaceLabel(place) {
  return place.detail ? `${place.name}, ${place.detail}` : place.name;
}

/**
 * The countries InGo operates in (its Settings, Regional; InGo ADR 0037), as
 * a label might name one: each name and code, lower case. Read once a
 * session; empty until then, and when it cannot be.
 */
let countryWords = [];

async function loadCountryWords() {
  try {
    const region = await publicSettings.region();
    const words = (region?.countries ?? []).flatMap((c) => [c.name, c.code]).map((w) => String(w).trim().toLowerCase());
    countryWords = words.filter(Boolean);
  } catch {
    // Not reachable: what is known stands.
  }

  return countryWords;
}

/** A label that is only a country we operate in, or "Uganda, Africa". */
function isCountryOnly(s, words) {
  const lower = s.toLowerCase().replace(/\s*,\s*africa$/, '');

  return words.includes(lower);
}

/** "Road, Uganda" without the country, when the country is one of ours. */
function withoutCountry(s, words) {
  const parts = s.split(',');
  const last = parts.length > 1 ? parts[parts.length - 1].trim().toLowerCase() : '';

  return words.includes(last) ? parts.slice(0, -1).join(',').trim() : s;
}

/** Country-only / too-coarse labels we must not use as pickup. */
function isCoarsePlaceLabel(line, words = countryWords) {
  const s = String(line || '')
    .trim()
    .toLowerCase()
    .replace(/\.$/, '');
  if (!s) return true;
  // "Zimbabwe", "UG", "Uganda, Africa": a country with nothing more specific
  return isCountryOnly(s, words);
}

/**
 * Unrecognised / unhelpful reverse-geocode labels common in gated communities.
 * Prefer a nearby named suburb / landmark instead (inDrive-style).
 */
export function isUnrecognizedPlaceLabel(line, words = countryWords) {
  const s = String(line || '').trim();
  if (!s || isCoarsePlaceLabel(s, words)) return true;
  // "Unnamed Road, Zimbabwe" / "Unnamed Street" / "Unnamed Road"
  if (/\bunnamed\b/i.test(s)) return true;
  // Plus codes alone (e.g. "5G8F+XX Zimbabwe") — not human-usable for dispatch
  if (/^[a-z0-9]{4,}\+[a-z0-9]{2,}\b/i.test(s) && !/,/.test(s.split('+')[0] || '')) {
    const withoutPlus = s.replace(/^[a-z0-9+]+\s*,?\s*/i, '').trim();
    if (!withoutPlus || isCoarsePlaceLabel(withoutPlus, words)) return true;
  }
  const bare = withoutCountry(s, words).toLowerCase();
  if (/^[a-z0-9]{4,}\+[a-z0-9]{2,}$/i.test(bare)) return true;
  // Bare "Road" / "Street" with no name
  if (/^(road|street|avenue|drive|lane|close|way)$/i.test(bare)) return true;
  return false;
}

/**
 * @typedef {{ id: string, label: string, lat: number, lng: number }} AddressSuggestion
 */

/**
 * Answers already had, for ten minutes (the server keeps them as long): a
 * backspace or the same words typed again cost no network, which on a poor
 * connection is most of the wait (Rio, 12 Oct 2026: "it tends too be too
 * slow on poor network"). Only answers are kept, never a failure.
 */
const SUGGESTIONS_KEPT_MS = 10 * 60 * 1000;
const SUGGESTIONS_KEPT_MAX = 60;
const suggestionsKept = new Map();
/**
 * The searches on the wire, by the same key: the page's own look-up of the
 * typed words waits for the box's search of them rather than sending a second
 * (on a poor line both went out, 12 Oct 2026).
 */
const suggestionsAsked = new Map();

/** Unicode's combining accents, U+0300 to U+036F: what NFD splits off a letter. */
const ACCENTS = new RegExp(`[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`, 'g');

function suggestionsKey(q, near) {
  const at = near ? `${near.lat.toFixed(2)},${near.lng.toFixed(2)}` : '';

  return `${q.toLowerCase()}|${at}`;
}

/** Forget every kept answer (a test's fresh start). */
export function forgetAddressSuggestions() {
  suggestionsKept.clear();
  suggestionsAsked.clear();
  namesKept.clear();
}

/** Words of a label or of what is typed: lower case, accents and punctuation gone. */
function wordsOf(text) {
  return String(text || '')
    .normalize('NFD')
    .replace(ACCENTS, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/**
 * The suggestions still matching what is typed now: each typed word begins
 * a word of the label ("borrowd" keeps "Borrowdale Road"). Shown at once as
 * letters are added, while the server is asked, so a slow connection never
 * leaves an empty box behind a list that already had the answer.
 *
 * @param {AddressSuggestion[]} suggestions
 * @param {string} typed
 * @returns {AddressSuggestion[]}
 */
export function narrowSuggestions(suggestions, typed) {
  const wanted = wordsOf(typed);
  if (wanted.length === 0) return [];

  return suggestions.filter((s) => {
    const have = wordsOf(s.label);
    return wanted.every((w) => have.some((h) => h.startsWith(w)));
  });
}

/**
 * **Address typeahead** — pickup / destination search boxes
 * (**`AddressSuggestInput`**, on **`/request-delivery`**): InGo's place
 * search, each hit with its pin, so the screen places it as chosen with no
 * second lookup. Empty without an InGo session.
 *
 * Nearest first when the screen knows a point to measure from (`near`: the
 * pickup, else where the person stands). Rio, 5 Oct 2026: "people are more
 * likely to make mistakes"; "Seeta" listed Entebbe's Seeta above Mukono's for
 * a client standing in Mukono's.
 *
 * @param {string} query
 * @param {{ limit?: number, near?: { lat: number, lng: number } | null }} [options]
 * @returns {Promise<AddressSuggestion[]>}
 */
export async function fetchAddressAutocompleteSuggestions(query, options = {}) {
  const limit = Math.min(8, Math.max(1, Number(options.limit) || 5));
  const q = String(query || '').trim();
  if (q.length < 2) return [];

  const near = options.near && Number.isFinite(options.near.lat) && Number.isFinite(options.near.lng) ? options.near : null;
  const key = suggestionsKey(q, near);
  const found = keptFor(key) ?? (await sharedSearch(q, near, key, true));

  return found ? found.slice(0, limit) : [];
}

/** The places kept for these words from this point, while still fresh; null otherwise. */
function keptFor(key) {
  const kept = suggestionsKept.get(key);

  return kept && Date.now() - kept.at < SUGGESTIONS_KEPT_MS ? kept.found : null;
}

/**
 * The search for these words from this point: the one already on the wire,
 * whoever sent it (the box, or the page placing the typed words), else a new
 * one. On a poor line both went out, one after the other (12 Oct 2026).
 */
function sharedSearch(q, near, key, typeAhead) {
  if (!suggestionsAsked.has(key)) {
    suggestionsAsked.set(key, askSuggestions(q, near, key, typeAhead).finally(() => suggestionsAsked.delete(key)));
  }

  return suggestionsAsked.get(key);
}

/** One search on the wire; its places nearest first, kept when it answers, null when it fails. */
async function askSuggestions(q, near, key, typeAhead) {
  // The server ranks places near `near` first (InGo ADR 0054); the sort below
  // still holds when it searched without (Nominatim behind it).
  const ingo = await ingoPlaces(() => booking.searchPlaces(q, { near, typeAhead }));
  if (!ingo) return null;

  const found = ingo.value.map((place, i) => ({
    id: `ingo-${i}-${place.latitude}-${place.longitude}`,
    label: ingoPlaceLabel(place),
    lat: place.latitude,
    lng: place.longitude,
  }));
  if (near) {
    const away = (s) => haversineKm(near.lat, near.lng, s.lat, s.lng) ?? Infinity;
    found.sort((a, b) => away(a) - away(b));
  }

  suggestionsKept.delete(key);
  suggestionsKept.set(key, { at: Date.now(), found });
  // The oldest goes first: a Map keeps the order things were put in.
  if (suggestionsKept.size > SUGGESTIONS_KEPT_MAX) suggestionsKept.delete(suggestionsKept.keys().next().value);

  return found;
}

/**
 * **GPS → readable address** (e.g. device location label): the place InGo
 * finds at the pin, or '' when it finds none. Callers:
 * **`devicePickupLocation.js`**, customer/driver flows that show “you are here” text.
 */
export async function reverseGeocodeLatLng(latitude, longitude) {
  return (await namePlace(latitude, longitude)).label;
}

/** Names already had, by the point to about eleven metres (as the server keeps them). */
const NAMES_KEPT_MAX = 100;
const namesKept = new Map();

/**
 * **A point's name, and whether InGo delivers there** (InGo ADR 0055: named
 * wherever the maps reach). `label` is '' when the maps know no place there.
 * `failed`: the server was not reached in time, so asking again may name it;
 * on a weak line it often does (12 Oct 2026, Rio: "sometimes it use codinates
 * not the building"). Only answers are kept, never a failure.
 *
 * @param {number} latitude
 * @param {number} longitude
 * @param {{ timeoutMs?: number }} [options] one try that gives up early
 * @returns {Promise<{ label: string, served: boolean, failed: boolean }>}
 */
export async function namePlace(latitude, longitude, { timeoutMs } = {}) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return { label: '', served: true, failed: false };
  }

  const key = `${lat.toFixed(4)},${lng.toFixed(4)}`;
  if (namesKept.has(key)) return namesKept.get(key);

  // Read now, so a caller judging the label (`isUnrecognizedPlaceLabel`) knows our countries.
  const [ingo] = await Promise.all([ingoPlaces(() => booking.reversePlace(lat, lng, { timeoutMs })), loadCountryWords()]);
  if (!ingo) return { label: '', served: true, failed: true };

  const answer = { label: ingo.value.place ? ingoPlaceLabel(ingo.value.place) : '', served: ingo.value.served, failed: false };
  namesKept.set(key, answer);
  // The oldest goes first: a Map keeps the order things were put in.
  if (namesKept.size > NAMES_KEPT_MAX) namesKept.delete(namesKept.keys().next().value);

  return answer;
}

/**
 * **Typed address → map coordinates** (route pins, distance): the first of
 * InGo's places for it, or, given a point to measure from (`near`), the
 * nearest of them, as the suggestions are ordered (5 Oct 2026: "seeta" typed
 * and never picked placed Entebbe's Seeta, the search's first, 70 km from a
 * client with three nearer). Caller: **`RequestDeliveryPage`**.
 *
 * @param {string} query
 * @param {{ near?: { lat: number, lng: number } | null }} [options]
 * @returns {Promise<{ lat: number, lng: number } | null>}
 */
export async function forwardGeocodeAddress(query, options = {}) {
  const q = String(query || '').trim();
  if (!q) return null;
  const words = await loadCountryWords();
  // Country-only queries resolve to a national centroid — useless for routes.
  if (isCoarsePlaceLabel(q, words)) return null;
  // "Unnamed Road" forward-geocodes unpredictably and caused ~300km fake distances.
  if (isUnrecognizedPlaceLabel(q, words)) return null;

  // "lat, lng" from GPS fallback — use exact coordinates (no geocode round-trip).
  const coordMatch = q.match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/);
  if (coordMatch) {
    const lat = Number(coordMatch[1]);
    const lng = Number(coordMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
      return { lat, lng };
    }
  }

  // The same search as the address box's, shared with it: the places it kept
  // for these words from this point, or the search of them already on the
  // wire, or a new one (with a page's patience, not a suggestion's). Nearest
  // first when there is a point, the search's first otherwise.
  const near = options.near && Number.isFinite(options.near.lat) && Number.isFinite(options.near.lng) ? options.near : null;
  const key = suggestionsKey(q, near);
  const found = keptFor(key) ?? (await sharedSearch(q, near, key, false));
  if (!found || found.length === 0) return null;

  return { lat: found[0].lat, lng: found[0].lng };
}
